import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Shippings } from '../../../types';
import { CheckoutActions } from '../../../checkout-store/actions';
import * as fromCheckoutReducer from '../../../checkout-store/reducers/checkout.reducer';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntil, finalize } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { FormService } from '@pages/checkout-app/services';
import { ToastService } from '@storybook/services/toast/toast.service';
import { PdqToastComponent } from '@storybook/components/toast/toast.component';

@Component({
  selector: 'app-add-checkout',
  templateUrl: './add-checkout.html',
  styleUrls: ['./add-checkout.scss'],
  standalone: false,
})
export class AddCheckout implements OnInit, OnDestroy {
  isLoading = false;
  duplicateValues: any;
  private destroyed$ = new Subject<boolean>();

  constructor(
    private store: Store<fromCheckoutReducer.State>,
    private detectSuccessOrFailure$: Actions,
    private route: ActivatedRoute,
    private formService: FormService,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.detectStoreActions();
    this.formService.onDuplicate.pipe(takeUntil(this.destroyed$)).subscribe((checkoutId) => {
      if (checkoutId) {
        this.isLoading = true;
        this.duplicateShipping(checkoutId);
      }
    });

    const urlParams = new URLSearchParams(window.location.search);
    const duplicateId = urlParams.get('did');
    if (duplicateId) {
      this.formService.onDuplicate.next(duplicateId);
    }
  }

  detectStoreActions() {
    this.detectSuccessOrFailure$
      .pipe(takeUntil(this.destroyed$), ofType(CheckoutActions.addNewShippingsCheckoutSuccess))
      .subscribe(() => {
        this.store.dispatch(CheckoutActions.loadCheckouts());
        const action = this.duplicateValues ? 'duplicated' : 'added';
        this.isLoading = false;
        this.toastService.show(PdqToastComponent, {
          message: `Delivery method has been ${action}`,
          mode: 'info',
          duration: 5000,
        });
      });

    this.detectSuccessOrFailure$
      .pipe(takeUntil(this.destroyed$), ofType(CheckoutActions.addNewShippingsCheckoutFailure))
      .subscribe((res) => {
        this.isLoading = false;
        const stateMsg = res?.type?.split(']')[1];
        this.toastService.show(PdqToastComponent, {
          message: res?.errorMessage ?? `${stateMsg}`,
          mode: 'danger',
          duration: 5000,
        });
      });
  }

  duplicateShipping(checkoutId: string) {
    this.formService
      .loadCheckoutById(checkoutId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((values) => {
        this.duplicateValues = Object.assign({
          ...values,
          name: `Copy of - ${values.name}`,
          id: null,
          shippingMethodId: null,
          shipmentExtrasId: null,
          shipmentExtras: { ...values.shipmentExtras, id: null },
          variants: [],
        });
      });
  }

  private addNewCheckout(shippings: Shippings): void {
    this.isLoading = true;
    this.store.dispatch(CheckoutActions.addNewShippingsCheckout({ shippings }));
  }

  onSubmit(values: Shippings): void {
    this.addNewCheckout({ ...values });
  }

  ngOnDestroy() {
    this.formService.onDuplicate.next(null);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
